import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints } from "../config/api";
import { fetchAllOrgUsers } from "../utils/fetchOrgUsers";

const COLUMN_COUNT = 4;

const shimmerCellStyle = {
  height: "20px",
  borderRadius: "4px",
  background: "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)",
  backgroundSize: "200% 100%",
  animation: "orgDashVisibilityShimmer 1.5s ease-in-out infinite",
};

const ShimmerRow = () => (
  <tr>
    {[...Array(COLUMN_COUNT)].map((_, i) => (
      <td key={i} style={{ padding: "12px 14px", border: "1px solid #e5e7eb" }}>
        <div style={shimmerCellStyle} />
      </td>
    ))}
  </tr>
);

const OrgDashboardVisibility = ({ onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    const fetchOrgUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const nextData = await fetchAllOrgUsers(endpoints.orgsUsers, { limit: 100 });
        setData(nextData);
      } catch (err) {
        console.error("Error fetching org users", err);
        setError(getErrorMessage(err, "Failed to load users"));
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgUsers();
  }, []);

  const allRows = useMemo(
    () =>
      data.flatMap((org) =>
        (org.users || []).map((user) => ({
          organization_id: org.organization_id,
          org_name: org.name || "—",
          ...user,
        }))
      ),
    [data]
  );

  const rows = useMemo(() => {
    if (!search.trim()) return allRows;
    const q = search.trim().toLowerCase();
    return allRows.filter(
      (row) =>
        (row.org_name && String(row.org_name).toLowerCase().includes(q)) ||
        (row.name && String(row.name).toLowerCase().includes(q)) ||
        (row.email && String(row.email).toLowerCase().includes(q))
    );
  }, [allRows, search]);

  const updateHideOrgDashboardsInState = (userId, hideOrgDashboards) => {
    setData((prev) =>
      prev.map((org) => ({
        ...org,
        users: (org.users || []).map((user) =>
          user.user_id === userId ? { ...user, hide_org_dashboards: hideOrgDashboards } : user
        ),
      }))
    );
  };

  const handleToggleHideOrgDashboards = async (row) => {
    const userId = row.user_id;
    if (!userId || actionLoading[userId]) return;

    const previous = row.hide_org_dashboards === true;
    const next = !previous;
    setActionError(null);
    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    updateHideOrgDashboardsInState(userId, next);

    try {
      const response = await axios.patch(
        endpoints.authHideOrgDashboards,
        { userId, hide_org_dashboards: next },
        {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const updatedUserId = response?.data?.data?.user_id || userId;
      const updatedHide =
        typeof response?.data?.data?.hide_org_dashboards === "boolean"
          ? response.data.data.hide_org_dashboards
          : next;
      updateHideOrgDashboardsInState(updatedUserId, updatedHide);
    } catch (err) {
      console.error("Error updating org dashboard visibility", err);
      updateHideOrgDashboardsInState(userId, previous);
      setActionError(getErrorMessage(err, "Failed to update org dashboard visibility"));
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const closeButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginBottom: "20px",
    alignSelf: "flex-start",
  };

  const titleStyle = {
    color: "#3730a3",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "8px",
  };

  const subtitleStyle = {
    color: "#4b5563",
    fontSize: "14px",
    lineHeight: "1.5",
    marginTop: 0,
    marginBottom: "20px",
    maxWidth: "720px",
  };

  const searchInputStyle = {
    width: "100%",
    maxWidth: "400px",
    padding: "12px 14px 12px 40px",
    fontSize: "14px",
    border: "1px solid #3730a3",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#1f2937",
    outline: "none",
    boxSizing: "border-box",
  };

  const searchWrapperStyle = {
    position: "relative",
    marginBottom: "20px",
    width: "100%",
    maxWidth: "400px",
  };

  const searchIconStyle = {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
    fontSize: "18px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "12px 14px",
    backgroundColor: "#3730a3",
    color: "#fff",
    fontWeight: "600",
    border: "1px solid #3730a3",
  };

  const tdStyle = {
    padding: "12px 14px",
    border: "1px solid #e5e7eb",
    color: "#1f2937",
  };

  const groupHeaderRowStyle = {
    backgroundColor: "#eef2ff",
  };

  const groupHeaderCellStyle = {
    padding: "10px 14px",
    border: "1px solid #c7d2fe",
    color: "#312e81",
    fontWeight: 700,
    letterSpacing: "0.2px",
  };

  const trEvenStyle = {
    backgroundColor: "#f9fafb",
  };

  const switchTrackStyle = {
    position: "relative",
    display: "inline-block",
    width: "36px",
    height: "20px",
    borderRadius: "999px",
    transition: "background-color 0.15s ease",
    flexShrink: 0,
  };

  const switchThumbStyle = {
    position: "absolute",
    top: "2px",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
    transition: "left 0.15s ease",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <style>
        {`@keyframes orgDashVisibilityShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }`}
      </style>
      {onClose && (
        <button style={closeButtonStyle} type="button" onClick={onClose}>
          Close
        </button>
      )}
      <h2 style={titleStyle}>Revoke Own Org Visibility</h2>
      <p style={subtitleStyle}>
        Turn off a user's switch to hide dashboards shared with their whole organization.
        Dashboards assigned directly to them or created by them stay visible.
      </p>

      {!loading && !error && (
        <div style={searchWrapperStyle}>
          <span style={searchIconStyle} aria-hidden>⌕</span>
          <input
            type="search"
            placeholder="Search by organization, name, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={searchInputStyle}
            aria-label="Search users"
          />
        </div>
      )}

      {error && <p style={{ color: "#b91c1c", marginBottom: "12px" }}>{error}</p>}
      {!error && actionError && (
        <p style={{ color: "#b91c1c", marginBottom: "12px" }}>{actionError}</p>
      )}

      <div style={{ width: "100%", overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Organization</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, whiteSpace: "nowrap" }}>Org Dashboards</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(8)].map((_, i) => (
                  <ShimmerRow key={i} />
                ))}
              </>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} style={{ ...tdStyle, textAlign: "center" }}>
                  {search.trim() ? "No users match your search." : "No users found."}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const previousRow = rows[idx - 1];
                const isNewOrgGroup =
                  idx === 0 || previousRow?.organization_id !== row.organization_id;
                const hidden = row.hide_org_dashboards === true;
                const busy = !!actionLoading[row.user_id];

                return (
                  <React.Fragment key={`${row.organization_id}-${row.user_id}`}>
                    {isNewOrgGroup && (
                      <tr style={groupHeaderRowStyle}>
                        <td colSpan={COLUMN_COUNT} style={groupHeaderCellStyle}>
                          {row.org_name}
                        </td>
                      </tr>
                    )}
                    <tr style={idx % 2 === 1 ? trEvenStyle : undefined}>
                      <td style={tdStyle}>{row.org_name}</td>
                      <td style={tdStyle}>{row.email ?? "—"}</td>
                      <td style={tdStyle}>{row.name ?? "—"}</td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: busy ? "not-allowed" : "pointer",
                          }}
                          onClick={() => handleToggleHideOrgDashboards(row)}
                        >
                          <span
                            role="switch"
                            aria-checked={!hidden}
                            aria-label={`Show org dashboards for ${row.email || row.name || row.user_id}`}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === " " || e.key === "Enter") {
                                e.preventDefault();
                                handleToggleHideOrgDashboards(row);
                              }
                            }}
                            style={{
                              ...switchTrackStyle,
                              backgroundColor: hidden ? "#d1d5db" : "#3730a3",
                              opacity: busy ? 0.6 : 1,
                            }}
                          >
                            <span style={{ ...switchThumbStyle, left: hidden ? "2px" : "18px" }} />
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                            {busy ? "Updating..." : hidden ? "Hidden" : "Shown"}
                          </span>
                        </span>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrgDashboardVisibility;
